import {
  BookingRecord,
  BookingStatus,
  BookingEventRecord,
  PayoutRecord,
  NotificationRecord,
  ChatMessageRecord
} from '../types';
import { apiFetch as fetch } from './apiConfig';
import { auth } from './firebase';
import {
  isDemoCompanionProfile,
  DEMO_COMPANION_BOOKING_MESSAGES,
  getDemoCompanionBookingMessage,
} from '../data/companionsData';

async function getFirebaseAuthHeaders(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) throw new Error('Please sign in again to continue.');
  const token = await user.getIdToken();
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
}

async function callBookingFunction<T>(
  name: string,
  method: 'GET' | 'POST',
  body?: Record<string, unknown> | CreateBookingPayload,
  query?: Record<string, string>
): Promise<T> {
  const user = auth.currentUser;
  if (!user) throw new Error('Please sign in again to continue.');
  const base = String(import.meta.env.VITE_FUNCTIONS_BASE_URL || 'https://asia-south1-nifty-cat-7cjpc.cloudfunctions.net').replace(/\/+$/, '');
  const url = new URL(`${base}/${name}`);
  Object.entries(query || {}).forEach(([key, value]) => url.searchParams.set(key, value));
  const token = await user.getIdToken();
  const response = await fetch(url.toString(), {
    method,
    headers: { Authorization: `Bearer ${token}`, ...(method === 'POST' ? { 'Content-Type': 'application/json' } : {}) },
    ...(method === 'POST' ? { body: JSON.stringify(body || {}) } : {}),
  });
  const result = await response.json();
  if (!response.ok || result.success !== true) throw new Error(result.error || result.code || `Booking service failed (HTTP ${response.status}).`);
  return result as T;
}

export interface CreateBookingPayload {
  clientId: string;
  clientName: string;
  clientEmail?: string;
  clientPhone?: string;
  companionId: string;
  companionName: string;
  companionImage?: string;
  bookingDate: string; // "YYYY-MM-DD"
  startTime: string; // "18:00"
  durationHours: number;
  locationName: string;
  latitude?: number;
  longitude?: number;
  googlePlaceId?: string;
  locationNote?: string;
  hourlyRate: number;
}

export async function createV2Booking(payload: CreateBookingPayload): Promise<{
  success: boolean;
  bookingId?: string;
  orderId?: string;
  amount?: number;
  currency?: string;
  keyId?: string;
  code?: string;
  error?: string;
  errorBn?: string;
  errorEn?: string;
  errorHi?: string;
}> {
  if (isDemoCompanionProfile(payload.companionId)) {
    const activeLang =
      (typeof window !== 'undefined' &&
        (window.localStorage?.getItem('pairup_language') as 'en' | 'bn' | 'hi' | null)) ||
      'en';
    return {
      success: false,
      code: 'DEMO_COMPANION_BOOKING_RESTRICTED',
      error: getDemoCompanionBookingMessage(activeLang),
      errorBn: DEMO_COMPANION_BOOKING_MESSAGES.bn,
      errorEn: DEMO_COMPANION_BOOKING_MESSAGES.en,
      errorHi: DEMO_COMPANION_BOOKING_MESSAGES.hi,
    };
  }
  try {
    const result = await callBookingFunction<any>('createBookingPaymentOrder', 'POST', payload);
    return result;
  } catch (err: any) {
    return { success: false, error: err.message || 'Failed to create booking' };
  }
}

export async function fetchV2Bookings(filters?: {
  clientId?: string;
  companionId?: string;
  status?: string;
}): Promise<{
  success: boolean;
  bookings: BookingRecord[];
  count: number;
  error?: string;
}> {
  try {
    const result = await callBookingFunction<{ success: true; bookings: BookingRecord[] }>('listMyBookings', 'GET');
    if (!Array.isArray(result.bookings)) throw new Error('Booking service returned an invalid record list.');
    const user = auth.currentUser;
    if (!user) throw new Error('Please sign in again to view bookings.');
    let bookings = result.bookings as BookingRecord[];
    if (filters?.clientId && filters.clientId !== user.uid) throw new Error('You can only view your own bookings.');
    if (filters?.companionId && filters.companionId !== user.uid) throw new Error('You can only view your own bookings.');
    if (filters?.status) bookings = bookings.filter((booking) => booking.status === filters.status);
    return { success: true, bookings, count: bookings.length };
  } catch (err: any) {
    return { success: false, bookings: [], count: 0, error: err.message || 'Failed to fetch bookings' };
  }
}

export async function fetchV2BookingById(id: string, requesterId?: string): Promise<{
  success: boolean;
  booking?: BookingRecord;
  events?: BookingEventRecord[];
  payout?: PayoutRecord;
  error?: string;
}> {
  try {
    if (requesterId && requesterId !== auth.currentUser?.uid) throw new Error('You can only view your own bookings.');
    return await callBookingFunction<{ success: true; booking: BookingRecord; events?: BookingEventRecord[]; payout?: PayoutRecord }>(
      'listMyBookings', 'GET', undefined, { bookingId: id }
    );
  } catch (err: any) {
    return { success: false, error: err.message || 'Failed to fetch booking details' };
  }
}

export interface PaymentProof {
  orderId?: string;
  paymentId?: string;
  signature?: string;
}

export async function createPaymentOrder(_referenceId: string): Promise<{
  success: boolean;
  data?: {
    paymentId: string;
    gatewayOrderId: string;
    orderId: string;
    signature: string;
    amount: number;
    currency: string;
  };
  error?: string;
}> {
  return {
    success: false,
    error: 'Direct order creation without booking parameters is disabled. Use createV2Booking (createBookingPaymentOrder) to create an authenticated Razorpay booking order.',
  };
}

export async function payV2Booking(
  id: string,
  _paymentMethod: string = 'UPI',
  proof?: PaymentProof
): Promise<{
  success: boolean;
  booking?: BookingRecord;
  message?: string;
  error?: string;
  code?: string;
}> {
  try {
    if (!id || !proof?.orderId || !proof?.paymentId || !proof?.signature) {
      return {
        success: false,
        code: 'MISSING_PAYMENT_PROOF',
        error: 'Verified Razorpay orderId, paymentId, and cryptographic signature are required to confirm a booking payment.',
      };
    }
    const verified = await callBookingFunction<{
      success: true;
      bookingId: string;
      status: string;
      paymentStatus: string;
      booking?: BookingRecord;
      message?: string;
    }>('verifyBookingPayment', 'POST', {
      bookingId: id,
      razorpay_order_id: proof.orderId,
      razorpay_payment_id: proof.paymentId,
      razorpay_signature: proof.signature,
      razorpayOrderId: proof.orderId,
      razorpayPaymentId: proof.paymentId,
      razorpaySignature: proof.signature,
    });
    return {
      success: true,
      booking: verified.booking,
      message: verified.message || 'Booking payment verified and confirmed.',
    };
  } catch (err: any) {
    return { success: false, error: err.message || 'Payment verification failed' };
  }
}

export async function acceptV2Booking(
  id: string,
  _companionId: string
): Promise<{
  success: boolean;
  booking?: BookingRecord;
  message?: string;
  error?: string;
}> {
  try {
    return await callBookingFunction<{ success: true; booking: BookingRecord; message?: string }>('acceptMyBooking', 'POST', { bookingId: id });
  } catch (err: any) {
    return { success: false, error: err.message || 'Failed to accept booking' };
  }
}

export async function declineV2Booking(
  id: string,
  companionId: string,
  reason?: string
): Promise<{
  success: boolean;
  booking?: BookingRecord;
  message?: string;
  error?: string;
}> {
  void id; void companionId; void reason;
  return { success: false, error: 'Declining a paid booking is unavailable until the authorized Razorpay refund workflow is connected. No booking status or payment was changed.' };
}

export async function checkInV2Booking(
  id: string,
  role: 'client' | 'companion'
): Promise<{
  success: boolean;
  booking?: BookingRecord;
  message?: string;
  error?: string;
}> {
  try {
    void role;
    return await callBookingFunction<{ success: true; booking: BookingRecord; message?: string }>('checkInMyBooking', 'POST', { bookingId: id });
  } catch (err: any) {
    return { success: false, error: err.message || 'Check-in failed' };
  }
}

export async function sendMeetingStartOtp(
  id: string
): Promise<{
  success: boolean;
  expiresAt?: string;
  message?: string;
  error?: string;
}> {
  try {
    const headers = await getFirebaseAuthHeaders();
    const res = await fetch(`/api/v2/bookings/${encodeURIComponent(id)}/start-otp/send`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ bookingId: id }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.success !== true) {
      return {
        success: false,
        error: data.error || data.message || `Failed to send Meeting Start OTP (HTTP ${res.status}).`,
      };
    }
    return {
      success: true,
      expiresAt: data.expiresAt,
      message: data.message || "Meeting Start OTP sent via APITXT SMS to the client's registered mobile number.",
    };
  } catch (err: any) {
    return { success: false, error: err.message || 'Failed to send Meeting Start OTP' };
  }
}

export async function verifyMeetingStartOtp(
  id: string,
  otp: string
): Promise<{
  success: boolean;
  booking?: BookingRecord;
  meetingStartedAt?: string;
  countdownEndsAt?: string;
  message?: string;
  error?: string;
}> {
  try {
    const headers = await getFirebaseAuthHeaders();
    const res = await fetch(`/api/v2/bookings/${encodeURIComponent(id)}/start-otp/verify`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ otp: otp.trim() }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.success !== true) {
      return {
        success: false,
        error: data.error || data.message || `Meeting Start OTP verification failed (HTTP ${res.status}).`,
      };
    }
    return {
      success: true,
      booking: data.booking,
      meetingStartedAt: data.meetingStartedAt,
      countdownEndsAt: data.countdownEndsAt,
      message: data.message || 'Meeting Start OTP verified! Meeting is now IN_PROGRESS and countdown has started.',
    };
  } catch (err: any) {
    return { success: false, error: err.message || 'Failed to verify Meeting Start OTP' };
  }
}

export async function getBookingCompletionOtp(
  id: string,
  clientId?: string
): Promise<{
  success: boolean;
  otp?: string;
  expiresAt?: string;
  message?: string;
  error?: string;
}> {
  try {
    const headers = await getFirebaseAuthHeaders();
    const v2Res = await fetch(`/api/v2/bookings/${encodeURIComponent(id)}/completion-otp/send`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ bookingId: id, slideConfirmed: true }),
    });
    if (v2Res.ok) {
      const v2Data = await v2Res.json().catch(() => ({}));
      if (v2Data.success === true) {
        return {
          success: true,
          expiresAt: v2Data.expiresAt,
          message: v2Data.message || 'Meeting Completion OTP sent via APITXT SMS to the client.',
        };
      }
    }
    if (clientId && clientId !== auth.currentUser?.uid) {
      throw new Error('Only the signed-in client or assigned companion can request meetup verification.');
    }
    const result = await callBookingFunction<{ success: true; expiresAt?: string; message: string }>('startMeetupVerification', 'POST', { meetupId: id });
    return { success: true, expiresAt: result.expiresAt, message: result.message };
  } catch (err: any) {
    return { success: false, error: err.message || 'Failed to generate completion OTP' };
  }
}

export async function verifyBookingCompletionOtp(
  id: string,
  companionId: string,
  otp: string
): Promise<{
  success: boolean;
  booking?: BookingRecord;
  payout?: PayoutRecord;
  message?: string;
  error?: string;
}> {
  try {
    if (companionId !== auth.currentUser?.uid) throw new Error('Only the signed-in companion can verify this meetup code.');
    const headers = await getFirebaseAuthHeaders();
    const v2Res = await fetch(`/api/v2/bookings/${encodeURIComponent(id)}/completion-otp/verify`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ otp: otp.trim() }),
    });
    if (v2Res.ok) {
      const v2Data = await v2Res.json().catch(() => ({}));
      if (v2Data.success === true) {
        return {
          success: true,
          booking: v2Data.booking,
          payout: v2Data.payout,
          message: v2Data.message,
        };
      }
    }
    const result = await callBookingFunction<{ success: true; status: string; payoutId?: string; payoutStatus?: string; message: string }>('verifyMeetupOtp', 'POST', { meetupId: id, otp });
    const detail = await callBookingFunction<{ success: true; booking: BookingRecord; payout?: PayoutRecord }>('listMyBookings', 'GET', undefined, { bookingId: id });
    return { success: true, booking: detail.booking, payout: detail.payout, message: result.message };
  } catch (err: any) {
    return { success: false, error: err.message || 'OTP verification failed' };
  }
}

export async function cancelV2Booking(
  id: string,
  cancelledBy: 'client' | 'companion',
  reason: string
): Promise<{
  success: boolean;
  booking?: BookingRecord;
  refundPolicyApplied?: string;
  refundAmount?: number;
  message?: string;
  error?: string;
}> {
  void id; void cancelledBy; void reason;
  return { success: false, error: 'Booking cancellation is unavailable until the persisted cancellation policy and authorized refund workflow are connected. No booking status or payment was changed.' };
}

export async function disputeV2Booking(
  id: string,
  reportedBy: 'client' | 'companion',
  reason: string
): Promise<{
  success: boolean;
  booking?: BookingRecord;
  message?: string;
  error?: string;
}> {
  void id; void reportedBy; void reason;
  return { success: false, error: 'Dispute submission is unavailable until a persisted case workflow is connected. No booking status was changed.' };
}

export async function fetchBookingChatMessages(bookingId: string): Promise<{
  success: boolean;
  messages: ChatMessageRecord[];
  isChatEnabled: boolean;
  bookingStatus: BookingStatus;
  error?: string;
}> {
  try {
    const headers = await getFirebaseAuthHeaders();
    const res = await fetch(`/api/v1/conversations/${encodeURIComponent(bookingId)}/messages`, {
      headers,
    });
    return await res.json();
  } catch (err: any) {
    return {
      success: false,
      messages: [],
      isChatEnabled: false,
      bookingStatus: 'PENDING_PAYMENT',
      error: err.message || 'Failed to fetch messages',
    };
  }
}

export async function sendBookingChatMessage(
  bookingId: string,
  senderId: string,
  senderName: string,
  senderRole: 'client' | 'companion',
  message: string
): Promise<{
  success: boolean;
  messageRecord?: ChatMessageRecord;
  error?: string;
}> {
  try {
    const user = auth.currentUser;
    if (!user || (senderId && senderId !== user.uid)) {
      throw new Error('Unauthorized: Sender identity does not match the signed-in user.');
    }
    const headers = await getFirebaseAuthHeaders();
    const res = await fetch(`/api/v1/conversations/${encodeURIComponent(bookingId)}/messages`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ senderId: user.uid, senderName, senderRole, message }),
    });
    return await res.json();
  } catch (err: any) {
    return { success: false, error: err.message || 'Failed to send message' };
  }
}

export async function fetchCompanionPayoutStats(companionId: string): Promise<{
  success: boolean;
  availableBalance: number;
  pendingBalance: number;
  totalEarned: number;
  totalPlatformFees: number;
  totalPaidOut: number;
  payouts: PayoutRecord[];
}> {
  try {
    const headers = await getFirebaseAuthHeaders();
    const res = await fetch(`/api/v1/payouts/companion/${encodeURIComponent(companionId)}`, {
      headers,
    });
    if (!res.ok) {
      throw new Error(`Failed to fetch payout stats (HTTP ${res.status})`);
    }
    return await res.json();
  } catch (err) {
    console.error('[fetchCompanionPayoutStats] Request failed:', err);
    return {
      success: false,
      availableBalance: 0,
      pendingBalance: 0,
      totalEarned: 0,
      totalPlatformFees: 0,
      totalPaidOut: 0,
      payouts: [],
    };
  }
}

export async function fetchUserNotifications(userId: string): Promise<{
  success: boolean;
  notifications: NotificationRecord[];
  unreadCount: number;
  error?: string;
}> {
  try {
    const user = auth.currentUser;
    if (!user || user.uid !== userId) throw new Error('You can only view your own notifications.');
    const headers = await getFirebaseAuthHeaders();
    const res = await fetch(`/api/v1/notifications/${encodeURIComponent(userId)}`, { headers });
    if (res.ok) return await res.json();
    throw new Error(`Notification service returned HTTP ${res.status}.`);
  } catch (err: any) {
    return { success: false, notifications: [], unreadCount: 0, error: err.message || 'Notification service unavailable.' };
  }
}

export async function markNotificationRead(id: string): Promise<boolean> {
  try {
    const headers = await getFirebaseAuthHeaders();
    const res = await fetch(`/api/v1/notifications/${encodeURIComponent(id)}/read`, {
      method: 'POST',
      headers,
    });
    return res.ok;
  } catch {
    return false;
  }
}

export async function fetchAdminV2Financials(): Promise<{
  success: boolean;
  stats: {
    totalGrossGMV: number;
    platformRevenue15: number;
    companionPayouts85: number;
    pendingPayouts: number;
    activeDisputesCount: number;
    totalBookingsCount: number;
    completedBookingsCount: number;
  };
  bookings: BookingRecord[];
}> {
  try {
    const headers = await getFirebaseAuthHeaders();
    const res = await fetch('/api/v1/admin/financials', { headers });
    if (res.ok) return await res.json();
    throw new Error('Failed to fetch admin financials');
  } catch {
    return {
      success: true,
      stats: {
        totalGrossGMV: 0,
        platformRevenue15: 0,
        companionPayouts85: 0,
        pendingPayouts: 0,
        activeDisputesCount: 0,
        totalBookingsCount: 0,
        completedBookingsCount: 0,
      },
      bookings: [],
    };
  }
}
